/* eslint-disable */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { makeStyles } from '@material-ui/core';
import ContentWrapper from 'components/Wrappers/ContentWrapper';
import RoundedButton from 'components/Shared/RoundedButton';
import UnsavedChangesGuard from 'components/Shared/UnsavedChangesGuard';
import useFormikSuccessReset from '../../../hooks/useFormikSuccessReset';
import { ALL_ROUTES } from 'config/routes';
import OrderForm from './widgets/OrderForm';
import CreateOrderDialog from 'components/Dialogs/CreateOrderDialog';
import BackButton from 'components/Shared/BackButton';
import { useDispatch, useSelector } from 'react-redux';
import EmptyPageLoader from '../../Shared/EmptyPageLoader';
import MetadataErrorWidget from './widgets/MetadataErrorWidget';
import {
  fetchCreateOrderMetadata,
  fetchOrder,
  submitOrder,
  REQUEST_STATUS,
  resetCreateStatus,
  resetOrderState,
  navigateToRoute,
} from '../../../redux_store';

const useStyles = makeStyles((theme) => ({
  contentContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: '100%',
  },
  formCard: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    marginBottom: theme.spacing(10),
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
      borderRadius: 12,
    },
  },
  scrollableContent: {
    flex: 1,
    overflow: 'auto',
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
  },
  formContent: {
    width: '100%',
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6, 4),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4, 3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2, 3),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
  backButton: {
    marginRight: 'auto',
  },
}));

const OrderCreate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { accountUuid, patientUuid, reorderUuid = 'newOrder' } = useParams();
  const { metadata: { currentAccount } } = useSelector((state) => state.user);
  const { en: { titles, btn, unsavedChanges } } = useSelector((state) => state.i18n);
  const {
    status,
    orderData,
    metadata,
    createStatus,
    backendErrors: reduxBackendErrors,
    metadataStatus,
  } = useSelector((state) => state.order);

  const [isOpenConfirmDialog, setOpenConfirmDialog] = useState(false);

  const metadataError = metadataStatus === REQUEST_STATUS.failed;
  const initialDataError = status === REQUEST_STATUS.failed;
  const loadingInitialData = status === REQUEST_STATUS.loading;
  const loading = createStatus === REQUEST_STATUS.loading;
  const isSuccess = createStatus === REQUEST_STATUS.succeeded;
  const isFailed = createStatus === REQUEST_STATUS.failed;
  const isMetadataLoading = metadataStatus === REQUEST_STATUS.loading;

  // with widget uses for Create New Order and Reorder Existed
  const isNewOrder = reorderUuid === 'newOrder';
  const hasOrderData = orderData?.providerUserUUID || isNewOrder;

  // Use hook for success reset
  const formikRef = useFormikSuccessReset(isSuccess, () => {
    dispatch(resetCreateStatus());
    dispatch(navigateToRoute({
      route: {
        path: ALL_ROUTES.order.list.path,
        title: ALL_ROUTES.order.list.additional.title,
        params: { currentAccountUuid: currentAccount.uuid },
        clearBreadcrumbs: true,
      },
      addDelay: true,
    }));
  });


  useEffect(() => {
    const accountId = accountUuid || currentAccount.uuid;
    dispatch(fetchCreateOrderMetadata({ accountUuid: accountId, patientUuid }));

    return () => {
      dispatch(resetOrderState());
    };
  }, [accountUuid, currentAccount.uuid, patientUuid, dispatch]);

  // Load existing order data for reorder
  useEffect(() => {
    if (!isNewOrder) {
      dispatch(fetchOrder({ reorderUuid, accountUuid: accountUuid || currentAccount.uuid } ));
    }
  }, [isNewOrder, reorderUuid, dispatch]);


  const handleOpenConfirmDialog = () => {
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
  };

  const createOrder = async (values) => {
    handleCloseConfirmDialog();
    dispatch(submitOrder({
      formValues: values,
      accountUuid: accountUuid || currentAccount.uuid,
      patientUuid,
    }));
  };

  useEffect(() => {
    if (createStatus === REQUEST_STATUS.failed) {
      setTimeout(() => {
        dispatch(resetCreateStatus());
      }, 2000);
    }
  }, [createStatus]);


  if (metadataError || initialDataError) {
    return <MetadataErrorWidget />;
  }


  if (isMetadataLoading || loadingInitialData || !hasOrderData) {
    return <EmptyPageLoader />;
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={orderData}
      validateOnBlur={false}
      onSubmit={createOrder}
    >
      {({ isValid, handleSubmit, dirty, isSubmitting, values }) => (
        <UnsavedChangesGuard
          hasUnsavedChanges={dirty}
          title={unsavedChanges.title}
          message={unsavedChanges.orderCreate}
          discardButtonText={unsavedChanges.leaveAnyway}
          keepEditingButtonText={unsavedChanges.keepEditing}
        >
          <ContentWrapper
            titleText={titles.createOrder}
            containerStyle={{ overflow: 'visible' }}
          >
          <div className={classes.contentContainer}>
            <div className={classes.formCard}>
              <div className={classes.scrollableContent}>
                <div className={classes.formContent}>
                  <OrderForm
                    values={values}
                    backendErrors={reduxBackendErrors}
                    metadata={metadata}
                  />
                </div>
              </div>
            </div>
            <div className={classes.actionsContainer}>
              <BackButton className={classes.backButton} />
              <RoundedButton
                variant="contained"
                color="primary"
                disabled={!isValid || isSubmitting || (reorderUuid === 'newOrder' && !dirty)}
                onClick={handleOpenConfirmDialog}
                isLoading={loading}
                isSuccess={isSuccess}
                isFailed={isFailed}
                className={classes.actionButton}
              >
                {btn.save}
              </RoundedButton>
            </div>
          </div>

          <CreateOrderDialog
            open={isOpenConfirmDialog}
            onClose={handleCloseConfirmDialog}
            onSubmit={handleSubmit}
          />
          </ContentWrapper>
        </UnsavedChangesGuard>
      )}
    </Formik>
  );
};

export default OrderCreate;
